import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import { Stack } from 'expo-router';
import { IconLink, theme, useResponsive, useSafeRouter } from '@sg/garnish';

import { HEADER_SCREEN_OPTIONS } from '../../options';

// ─────────────────────────────────────────────────────────────────────────────

export default function AccountTabLayout() {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  return (
    <Stack screenOptions={SCREEN_OPTIONS}>
      <Stack.Screen name="menu/index" options={{ headerShown: false }} />

      <Stack.Screen
        name="info/index"
        options={{
          headerShown: currentBreakpoint.isXS,
          title: formatMessage(messages.profile),
        }}
      />

      <Stack.Screen
        name="payment-and-gift-cards/index"
        options={{
          headerShown: currentBreakpoint.isXS,
          title: formatMessage(messages.payments),
        }}
      />

      <Stack.Screen
        name="credit-and-promo-codes/index"
        options={{
          headerShown: currentBreakpoint.isXS,
          title: formatMessage(messages.creditAndPromoCodes),
        }}
      />

      <Stack.Screen
        name="gift-cards/[[...giftCardId]]/index"
        options={{
          headerShown: currentBreakpoint.isXS,
          title: formatMessage(messages.giftCards),
        }}
      />

      <Stack.Screen
        name="addresses/index"
        options={{
          headerShown: currentBreakpoint.isXS,
          title: formatMessage(messages.addresses),
        }}
      />

      <Stack.Screen name="orders/index" options={{ headerShown: false }} />
      <Stack.Screen
        name="orders/[tabId]/index"
        options={{ headerShown: false }}
      />

      <Stack.Screen
        name="favorites/index"
        options={{
          headerShown: currentBreakpoint.isXS,
          title: formatMessage(messages.favorites),
        }}
      />

      <Stack.Screen
        name="dietary-restrictions/index"
        options={{
          headerShown: currentBreakpoint.isXS,
          title: formatMessage(messages.dietaryRestrictions),
        }}
      />

      <Stack.Screen
        name="notifications/index"
        options={{
          headerShown: currentBreakpoint.isXS,
          title: formatMessage(messages.notifications),
        }}
      />
    </Stack>
  );
}

// ─── Components ─────────────────────────────────────────────────────────────

const AccountHeaderBack = () => {
  const router = useSafeRouter();

  const handlePress = useCallback(() => {
    if (router?.canGoBack()) {
      router.back();
      return;
    }

    router?.navigate('/account/menu');
  }, [router]);

  return (
    <IconLink
      name="IconArrowLeft"
      style={styles.backButton}
      color={theme.colors.GREEN_1}
      accessibilityLabel="Back"
      onPress={handlePress}
    />
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SCREEN_OPTIONS = {
  ...HEADER_SCREEN_OPTIONS,
  headerLeft: () => <AccountHeaderBack />,
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  backButton: {
    paddingLeft: Platform.OS === 'web' ? theme.spacing['4'] : 0,
  },
});

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  profile: {
    defaultMessage: 'Account info',
    description: 'Account Tab | Profile screen title',
  },
  payments: {
    defaultMessage: 'Payments',
    description: 'Account Tab | Payments screen title',
  },
  creditAndPromoCodes: {
    defaultMessage: 'Credit & promo codes',
    description: 'Account Tab | Credit and promo codes screen title',
  },
  giftCards: {
    defaultMessage: 'Gift cards',
    description: 'Account Tab | Gift cards screen title',
  },
  addresses: {
    defaultMessage: 'Addresses',
    description: 'Account Tab | Addresses screen title',
  },
  favorites: {
    defaultMessage: 'Favorites',
    description: 'Account Tab | Favorites screen title',
  },
  dietaryRestrictions: {
    defaultMessage: 'Dietary restrictions',
    description: 'Account Tab | Dietary restrictions screen title',
  },
  notifications: {
    defaultMessage: 'Notifications',
    description: 'Account Tab | Notifications screen title',
  },
});
